// src/config/sujets.config.ts — SCE Issues
// Sujets (23/10, maquette https://claude.ai/artifact/2nCXTxokPHpFUFj96TdLjy).
//
// Les goulottes sont dessinées par la Méca et montées par les électriciens :
// c'est toujours la Méca qui corrige, mais Thibaut et ses équipes sont souvent
// gênés. Plutôt qu'un 5ᵉ métier (qui sortirait ces problèmes des chiffres Méca
// et demanderait son propre BE), un problème de goulotte est un problème MECA
// qui porte le sujet « Goulotte » :
//  - les coordinateurs Méca le pilotent comme les autres (Romuald, Sébastien) ;
//  - les RESPONSABLES du sujet aussi (Thibaut) : alertés, dans leur file, tous
//    les droits — sur ces problèmes seulement. Hugo, suppléant de Thibaut en
//    Élec / Autom, n'est pas concerné ;
//  - à la qualification, les familles du sujet remplacent celles du métier.
//
// Responsables et ouverture se règlent dans le panneau d'administration (bloc
// `roles` : `sujets`, `ouvertures.sujets`) ; les valeurs d'ici sont celles par
// défaut. Un test de droit sur un problème passe par `piloteLeProbleme`.

import type { Issue, Metier, Sujet } from '@/types/issue.types'
import { METIERS_FAMILLES, METIERS_LABELS_COURT } from './metiers.config'
import { getCoordinateurs, pilote, type Coordinateur } from './coordinateurs.config'
import { rolesCharges } from './registreRoles'

export interface DefinitionSujet {
  libelle: string
  /** Code court, en capitales comme ELEC, MECA… : bulles, sélecteur, badge. */
  libelleCourt: string
  /** Nom de la vue du tableau « Tous les problèmes ». */
  libellePluriel: string
  /** Le métier qui corrige : un problème de ce sujet est toujours de ce métier. */
  metier: Metier
  /** Familles proposées à la qualification, à la place de celles du métier. */
  familles: string[]
  /** Une ligne sous les bulles de la déclaration, quand le sujet est choisi. */
  aide: string
}

export const SUJETS_CONFIG: Record<Sujet, DefinitionSujet> = {
  Goulotte: {
    libelle: 'Goulotte',
    libelleCourt: 'GOULOTTE',
    libellePluriel: 'Goulottes',
    metier: 'MECA',
    // « Mal positionnée » : un câble sort au plus de 40 cm de la goulotte
    // jusqu'à l'élément câblé ; au-delà, la goulotte est mal placée.
    familles: [
      'Étude — goulotte non prévue ou mal définie',
      'Mal positionnée — câble à plus de 40 cm',
      'Section ou longueur inadaptée',
      'Collision avec une autre pièce',
    ],
    aide: 'Câbles et tubes d’air dans les modules de pompage. Traité par la Méca.',
  },
}

/** Responsables de chaque sujet, tant que le panneau n'a rien enregistré. */
export const RESPONSABLES_SUJET_DEFAUT: Record<Sujet, readonly { nom: string; upn: string }[]> = {
  Goulotte: [{ nom: 'Thibaut Hacquin', upn: 'thibaut.hacquin@sartorius.com' }],
}

/**
 * Le choix d'un sujet à la déclaration est-il ouvert à tous ? Fermé, seul
 * l'administrateur voit la bulle (recette en production). Le reste (droits,
 * files, familles) vaut dès qu'un problème porte un sujet.
 */
export const SUJETS_OUVERTS_DEFAUT = false

const enCoordinateur = (p: { nom: string; upn: string }): Coordinateur => ({ nom: p.nom, email: p.upn, upn: p.upn })

/** Responsables d'un sujet : ceux du panneau d'administration, sinon ceux d'ici. */
export function responsablesDuSujet(sujet: Sujet): Coordinateur[] {
  const r = rolesCharges()
  return (r ? r.sujets[sujet] : RESPONSABLES_SUJET_DEFAUT[sujet]).map(enCoordinateur)
}

/** Les sujets dont l'UPN est responsable (casse ignorée). */
export function sujetsDuResponsable(upn: string): Sujet[] {
  if (!upn) return []
  const cible = upn.toLowerCase()
  return (Object.keys(SUJETS_CONFIG) as Sujet[]).filter((s) =>
    responsablesDuSujet(s).some((c) => c.upn.toLowerCase() === cible),
  )
}

export function sujetsOuverts(): boolean {
  return rolesCharges()?.ouvertures.sujets ?? SUJETS_OUVERTS_DEFAUT
}

/** Familles proposées pour un problème : celles de son sujet, sinon celles de son métier. */
export function famillesDuProbleme(p: { metier: Metier; sujet?: Sujet }): string[] {
  return p.sujet ? SUJETS_CONFIG[p.sujet].familles : METIERS_FAMILLES[p.metier]
}

/**
 * L'UPN pilote-t-il CE problème (qualifier, modifier, annuler, clore) ?
 * Coordinateur ou suppléant de son métier, ou responsable de son sujet.
 */
export function piloteLeProbleme(upn: string, p: { metier: Metier; sujet?: Sujet }): boolean {
  if (pilote(upn, p.metier)) return true
  return !!p.sujet && sujetsDuResponsable(upn).includes(p.sujet)
}

/**
 * Ceux qu'on inscrit dans la colonne Coordinateur : titulaires du métier puis
 * responsables du sujet, sans doublon. Le flow 1 alerte cette colonne : un
 * problème de goulotte prévient ainsi Thibaut sans retoucher le flow.
 */
export function coordinateursDuProbleme(p: { metier: Metier; sujet?: Sujet }): Coordinateur[] {
  const tous = [...getCoordinateurs(p.metier), ...(p.sujet ? responsablesDuSujet(p.sujet) : [])]
  const vus = new Set<string>()
  return tous.filter((c) => {
    const cle = c.upn.toLowerCase()
    if (vus.has(cle)) return false
    vus.add(cle)
    return true
  })
}

// ─── Périmètre de l'accueil : des métiers et des sujets ───

/** Un métier ou un sujet : ce qu'un coordinateur suit à l'accueil. */
export type ClePerimetre = Metier | Sujet

/** Le problème entre-t-il dans ce périmètre ? Vide = tout (administrateur). */
export function dansPerimetre(p: Pick<Issue, 'metier' | 'sujet'>, cles: readonly ClePerimetre[]): boolean {
  if (cles.length === 0) return true
  return cles.includes(p.metier) || (!!p.sujet && cles.includes(p.sujet))
}

/**
 * Métiers et sujets dans l'ordre de l'interface : chaque sujet juste après le
 * métier qui le traite (ELEC · AUTOM · MECA · Goulotte · PROCESS). Un sujet dont
 * le métier n'est pas dans la liste (Thibaut : Goulotte sans MECA) va à la fin.
 */
export function rangeeMetiersEtSujets(metiers: readonly Metier[], sujets: readonly Sujet[]): ClePerimetre[] {
  const rangee: ClePerimetre[] = []
  for (const m of metiers) {
    rangee.push(m)
    rangee.push(...sujets.filter((x) => SUJETS_CONFIG[x].metier === m))
  }
  rangee.push(...sujets.filter((x) => !metiers.includes(SUJETS_CONFIG[x].metier)))
  return rangee
}

/** Le métier d'une clé : lui-même, ou celui qui traite le sujet (Goulotte : MECA). */
export function metierDeCle(cle: ClePerimetre): Metier {
  return cle in SUJETS_CONFIG ? SUJETS_CONFIG[cle as Sujet].metier : (cle as Metier)
}

/** « ELEC », « MECA », « GOULOTTE » : codes courts. */
export function libelleCle(cle: ClePerimetre): string {
  return cle in SUJETS_CONFIG ? SUJETS_CONFIG[cle as Sujet].libelleCourt : METIERS_LABELS_COURT[cle as Metier]
}
